import { useMemo, useState } from 'react'

import { MISSION_SORT_OPTIONS } from '../constants/sort'
import { collapseSectionItems } from '../domain/collapseSectionItems'
import { sortMissionItems, type MissionSortOption } from '../domain/sortMissionItems'
import {
  groupItemsByCategory,
  PASS_CATEGORY,
  TODAS_CATEGORY,
  type MissionListItem
} from '../lib/missionCategories'
import { useMissionsCategoryData } from '../hooks/useMissionsCategoryData'

import { useAppRouter } from '@/shared/lib/router'
import { useModalContext } from '@/shared/contexts/Modal'
import { Card, CardHeader, CardTitle, CardBody } from '@/shared/ui/Card'
import { Chip, ChipRail } from '@/shared/ui/ChipRail'
import { SortDropdown } from '@/shared/ui/SortDropdown'

import { MissionClaimBanner } from './MissionClaimBanner'
import { MissionListItemCard } from './MissionListItemCard'
import { MissionSectionHeader } from './MissionSectionHeader'
import { MissionModal } from './MissionModal'

export function MissionsList() {
  const { items, categories, claimableItems } = useMissionsCategoryData()
  const [activeCategoryId, setActiveCategoryId] = useState(TODAS_CATEGORY.id)
  const [sortOption, setSortOption] = useState<MissionSortOption>('inProgress')
  // Recolhido por padrão: com 24 objetivos, o bloco do Passe empurrava todo o
  // resto do feed para fora da tela. O estado vive aqui e não em storage — a
  // expansão dura a sessão da tela, e trocar de aba e voltar preserva a escolha.
  const [passExpanded, setPassExpanded] = useState(false)
  const { openModal } = useModalContext()
  const router = useAppRouter()

  const filteredItems = useMemo(() => {
    if (activeCategoryId === TODAS_CATEGORY.id) return items
    return items.filter(item => item.categoryIds.includes(activeCategoryId))
  }, [items, activeCategoryId])

  const sortedItems = useMemo(
    () => sortMissionItems(filteredItems, sortOption),
    [filteredItems, sortOption]
  )

  const sections = useMemo(
    () =>
      activeCategoryId === TODAS_CATEGORY.id
        ? groupItemsByCategory(sortedItems)
        : null,
    [activeCategoryId, sortedItems]
  )

  const handleSelectItem = (item: MissionListItem) => {
    if (item.kind === 'mission' && item.mission) {
      // A missão "Entre numa campanha": enquanto não concluída, clicar leva à tela
      // de campanhas (o ato de entrar). Uma vez concluída (coletável ou já
      // resgatada), abre o modal como as demais para permitir coletar a recompensa.
      if (item.mission.slug === 'join_campaign' && !item.completed) {
        router.push('/campanhas')
        return
      }

      openModal({ modal: MissionModal }, { missionId: item.mission.id })
      return
    }

    // Passe items: a specific single-mission objective opens that mission
    // directly; anything broader (quantity pool, other resource types) has
    // no single destination on this screen, so it goes to the pass's own
    // trail page instead — see tasks.md 4.6.
    const objective = item.passObjective
    if (
      objective?.mode === 'specific' &&
      objective.resource_type === 'Mission' &&
      objective.resource_ids.length === 1
    ) {
      openModal({ modal: MissionModal }, { missionId: objective.resource_ids[0] })
      return
    }

    router.push('/passe')
  }

  return (
    <div className="flex flex-col gap-4 w-full items-center min-w-0 max-w-full">
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <CardTitle>Missões</CardTitle>
        </CardHeader>
        <CardBody className="gap-3">
          <ChipRail>
            {categories.map(category => (
              <Chip
                key={category.id}
                active={category.id === activeCategoryId}
                onClick={() => setActiveCategoryId(category.id)}
              >
                {category.label}
              </Chip>
            ))}
          </ChipRail>

          <div className="flex items-center justify-between gap-2">
            <span className="font-sans text-sm text-gray-500">
              {sortedItems.length} missões
            </span>
            <SortDropdown
              value={sortOption}
              options={MISSION_SORT_OPTIONS}
              onChange={value => setSortOption(value as MissionSortOption)}
              className="shrink-0"
              labelClassName="hidden sm:inline"
            />
          </div>

          <MissionClaimBanner items={claimableItems} onSelect={handleSelectItem} />

          {sections
            ? sections.map(section => {
                // Só a seção do Passe recolhe. As demais (Diárias, Semanais,
                // Campanha, Resgatadas) seguem inteiras, com o header estático
                // de antes — o card pede o accordion só para o Passe.
                const collapsible = section.category.id === PASS_CATEGORY.id
                const { visibleItems, hiddenCount } = collapseSectionItems(
                  section.items,
                  { collapsible, expanded: passExpanded }
                )
                const contentId = `mission-section-${section.category.id}`

                return (
                  <div key={section.category.id} className="flex flex-col gap-2">
                    <MissionSectionHeader
                      category={section.category}
                      count={section.items.length}
                      {...(collapsible && {
                        expanded: passExpanded,
                        onToggle: () => setPassExpanded(current => !current),
                        contentId
                      })}
                    />
                    <div id={contentId} className="flex flex-col gap-2">
                      {visibleItems.map(item => (
                        <MissionListItemCard
                          key={item.id}
                          item={item}
                          onClick={() => handleSelectItem(item)}
                        />
                      ))}
                    </div>
                    {hiddenCount > 0 && (
                      <button
                        type="button"
                        onClick={() => setPassExpanded(true)}
                        aria-expanded={false}
                        aria-controls={contentId}
                        className="self-start px-1 font-sans text-sm font-bold text-[#4FA37A] underline-offset-2 hover:underline"
                      >
                        Ver mais {hiddenCount}{' '}
                        {hiddenCount === 1 ? 'objetivo' : 'objetivos'}
                      </button>
                    )}
                  </div>
                )
              })
            : sortedItems.map(item => (
                <MissionListItemCard
                  key={item.id}
                  item={item}
                  onClick={() => handleSelectItem(item)}
                />
              ))}
        </CardBody>
      </Card>
    </div>
  )
}
